import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ItemDTO, OrderDTO, OrderTotalsDTO, SellerSimpleSummaryDTO, SellerSummaryDTO,
} from '../../models/order';
import { Response } from '../../models/shared';
import TicketDTO from '../../models/ticket';
import baseQueryWithReauth from './base';
import EventDTO, { EventSalesReportDTO, EventAudienceDiscountDTO, EventSalesByUserDTO } from '../../models/event';
import { PollData } from '../../models/poll';
// eslint-disable-next-line import/no-cycle
import { ticketsApi } from './tickets';
import { UserEvent } from '../../models/user';
import { PaymentMethod, PaymentProvider } from '../../models/payment';
// eslint-disable-next-line import/no-cycle
import { eventsApi } from './events';
import OrderItem from '../../types/OrderItem';

export interface ItemData {
  name: string;
  last_name: string;
  person_id: string;
  ticket_id: string;
  ticket?: TicketDTO,
}
export interface PayWayTokenData {
  token: string; // Token ID from Decidir SDK after tokenizing card
  bin: string; // First 6 digits of card number
  payment_method_id: number; // 1=Visa, 15=Mastercard, 65=Amex
  installments?: number; // Optional, defaults to 1
}

export interface NavePaymentData {
  payment_request_id: string; // UUID v4 from backend payment request
  status: 'approved' | 'rejected' | 'pending' | 'cancelled' | 'error';
  payment_id?: string; // Payment ID from Nave SDK on success
}

export interface OrderData {
  email: string;
  phone?: string;
  instagram_user?: string;
  items: ItemData[];
  open_price_amount?: number;
  coupon_id?: string;
  poll_responses?: PollData[];
  is_manual?: boolean;
  skip_poll?: boolean;
  referral_code?: string | null;
  from_backoffice: boolean;
  is_seller_order: boolean;
  is_cashier_order?: boolean;
  cashier_id?: string;
  payment_method?: PaymentMethod;
  payment_provider?: PaymentProvider;
  reservation_code?: string;
  payway_token?: PayWayTokenData; // Required when payment_provider === "PAYWAY", forbidden otherwise
  nave_payment?: NavePaymentData; // Required when payment_provider === "NAVE", forbidden otherwise
}

export interface DecodedItemData {
  item_id: string;
  order_id: string;
  name: string;
  last_name: string;
  person_id: string;
  order_status: string;
  valid: boolean;
  marked_used_by: number;
  used_at: string;
  event_id: string,
  ticket_id: string,
  event?: EventDTO
  ticket?: TicketDTO
  accepted_on_decode: boolean;
  is_expired: boolean;
  seat_name?: string;
  audience_discount?: EventAudienceDiscountDTO | null;
  showtime_id: string | null;
}

export interface DecodedOrderQrData {
  order_id: string;
  custom_id: string;
  display_id: string;
  items: Array<{
    item_id: string;
    name: string;
    last_name: string;
    person_id: string;
    marked_used_by: number | null;
    used_at: string | null;
    ticket_id: string;
    ticket: TicketDTO | null;
    audience_discount: EventAudienceDiscountDTO | null;
  }>;
  valid: boolean;
}

export interface PartialOrderData {
  event_id: string;
  items: {
    person_id: string;
    ticket_id: string;
  }[];
}

export interface OrderReferralData {
  order: OrderDTO;
  orders_by_referral: { buyer_name: string, amount: number, created_at: string, items_count: number }[];
  progress: {
    progress_percentage: number;
    reimburse_amount: number;
    limit_amount: number;
  };
}

export interface DecodedSellerQRData {
  user_event: UserEvent;
}

const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['orders', 'qrcode', 'seller_summary'],
  endpoints: (build) => ({
    createOrder: build.mutation<Response<OrderDTO>, { order: OrderData, businessId: string, eventId: string }>({
      query: ({
        order, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'POST',
        body: {
          ...order,
        },
      }),
      invalidatesTags: ['orders', 'seller_summary'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ticketsApi.util.invalidateTags(['tickets']));
          dispatch(eventsApi.util.invalidateTags(['cashier_report']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    getEventOrders: build.query<Response<OrderDTO[]>, { businessId: string, eventId: string }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'GET',
      }),
    }),
    getEventOrderItems: build.query<OrderItem[] | null, { businessId: string, eventId: string, userId?: string }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId, userId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'GET',
        params: {
          flat: 'true',
          created_by_id: userId,
        },
      }),
      transformResponse: (response: Response<OrderItem[]>): (OrderItem[] | null) => response.data || null,
    }),
    getSellerOrders: build.query<Response<OrderDTO[]>, { businessId: string, eventId: string }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/data`,
        method: 'GET',
      }),
    }),
    getOrderById: build.query<OrderDTO | null, { orderId: string }>({
      providesTags: ['orders'],
      query: ({ orderId }) => ({
        url: `/orders/${orderId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<OrderDTO>): (OrderDTO | null) => response.data || null,
    }),
    getOrderByCustomId: build.query<OrderDTO | null, { customId: string }>({
      providesTags: ['orders'],
      query: ({ customId }) => ({
        url: `/orders/custom-id/${customId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<OrderDTO>): (OrderDTO | null) => response.data || null,
    }),
    getEventOrdersExport: build.query<void, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        params: {
          xlsx: true,
        },
        method: 'GET',
      }),
    }),
    decodeQrCode: build.query<(DecodedItemData | null), { data: string | null, personId?: string, eventId: string, businessId: string, hasOrderQr: boolean }>({
      providesTags: ['qrcode'],
      query: ({
        data, eventId, businessId, personId, hasOrderQr,
      }) => ({
        url: hasOrderQr ? `/businesses/${businessId}/events/${eventId}/decode-order` : `/businesses/${businessId}/events/${eventId}/decode`,
        params: {
          data: data || undefined,
          person_id: personId,
        },
        method: 'GET',
      }),
      transformResponse: (response: Response<DecodedItemData>): (DecodedItemData | null) => response.data || null,

    }),
    updateOrder: build.mutation<Response<OrderDTO>, { order: Partial<OrderDTO>, businessId: string, eventId: string, orderId: string }>({
      query: ({
        order, businessId, eventId, orderId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'PUT',
        body: { ...order, id: orderId },
      }),
      invalidatesTags: ['orders'],
    }),
    updateOrderStatus: build.mutation<Response<OrderDTO>, { newStatus: string, businessId: string, eventId: string, orderId: string }>({
      query: ({
        newStatus, businessId, eventId, orderId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/status`,
        method: 'PATCH',
        body: { status: newStatus },
      }),
      invalidatesTags: ['orders'],
    }),
    updateOrderItem: build.mutation<Response<ItemDTO>, { item: Partial<ItemDTO>, businessId: string, eventId: string, orderId: string, itemId: string }>({
      query: ({
        item, businessId, eventId, orderId, itemId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items`,
        method: 'PUT',
        body: { ...item, id: itemId },
      }),
      invalidatesTags: ['orders'],
    }),
    deleteOrderItem: build.mutation<Response<ItemDTO>, { businessId: string, eventId: string, orderId: string, itemId: string }>({
      query: ({
        businessId, eventId, orderId, itemId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['orders', 'seller_summary'],
    }),
    deleteOrder: build.mutation<Response<OrderDTO>, { businessId: string, eventId: string, orderId: string }>({
      query: ({
        businessId, eventId, orderId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['orders', 'seller_summary'],
    }),
    updateUsedOrderItem: build.mutation<Response<ItemDTO>, { businessId: string, eventId: string, orderId: string, itemId: string }>({
      query: ({
        businessId, eventId, orderId, itemId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items/${itemId}/used`,
        method: 'PATCH',
      }),
      invalidatesTags: ['qrcode'],
    }),
    sendOrderEmail: build.mutation<Response<void>, { businessId: string, eventId: string, customId: string }>({
      query: ({
        businessId, eventId, customId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${customId}/email`,
        method: 'POST',
      }),
    }),
    getSellerSummary: build.query<SellerSummaryDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: ['orders', 'seller_summary'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerSummaryDTO[]>): (SellerSummaryDTO[] | null) => response.data || null,
    }),
    getQuickSellerSummary: build.query<SellerSimpleSummaryDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: ['orders', 'seller_summary'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary-quick`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerSimpleSummaryDTO[]>): (SellerSimpleSummaryDTO[] | null) => response.data || null,
    }),
    getOrderTotals: build.query<OrderTotalsDTO | null, { businessId: string, eventId: string, order: OrderData }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId, order }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/amounts`,
        method: 'POST',
        body: order,
      }),
      transformResponse: (response: Response<OrderTotalsDTO>): (OrderTotalsDTO | null) => response.data || null,
    }),
    getOneSellerSummary: build.query<SellerSummaryDTO | null, { businessId: string, eventId: string, sellerId: string }>({
      providesTags: ['orders', 'seller_summary'],
      query: ({ businessId, eventId, sellerId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary/${sellerId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerSummaryDTO>): (SellerSummaryDTO | null) => response.data || null,
    }),
    getEventOrdersReport: build.query<EventSalesReportDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'orders' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/sales`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSalesReportDTO>): (EventSalesReportDTO | null) => response.data || null,
    }),
    getQRImage: build.query<{ qr_base64: string[] }, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/orders/${orderId}/qr-image`,
        method: 'GET',
      }),
      transformResponse: (response: Response<{ qr_base64: string[] }>): { qr_base64: string[] } => response.data || { qr_base64: [] },
    }),
    getOrderReferralData: build.query<OrderReferralData | null, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/orders/${orderId}/referral`,
        method: 'GET',
      }),
      transformResponse: (response: Response<OrderReferralData>): (OrderReferralData | null) => response.data || null,
    }),
    validateTicketExclusive: build.mutation<boolean, { businessId: string, eventId: string, order: PartialOrderData }>({
      query: ({ businessId, eventId, order }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/actions/ticket-exclusive`,
        method: 'POST',
        body: order,
      }),
      transformResponse: (response: Response<boolean>): boolean => response.data || false,
    }),
    getSellerSummaryXlsx: build.mutation<void, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary/xlsx`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `sellers_summary_${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}-${businessId}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    getEventSalesByUser: build.query<EventSalesByUserDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'orders' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/sales-by-user`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSalesByUserDTO[]>): (EventSalesByUserDTO[] | null) => response.data || null,
    }),
    createBatchManualOrders: build.mutation<Response<void>, { businessId: string, eventId: string, ticketId: string, name: string, amount: number }>({
      query: ({
        businessId, eventId, ticketId, name, amount,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/actions/batch-manual`,
        method: 'POST',
        body: {
          ticket_id: ticketId,
          name,
          amount,
        },
      }),
      invalidatesTags: ['orders'],
    }),
  }),
});

export const {
  useCreateOrderMutation,
  useGetEventOrdersQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  useGetEventOrdersExportQuery,
  useUpdateOrderItemMutation,
  useDeleteOrderItemMutation,
  useUpdateOrderStatusMutation,
  useDecodeQrCodeQuery,
  useUpdateUsedOrderItemMutation,
  useSendOrderEmailMutation,
  useGetOrderByCustomIdQuery,
  useGetSellerOrdersQuery,
  useGetSellerSummaryQuery,
  useGetOneSellerSummaryQuery,
  useGetOrderTotalsQuery,
  useGetEventOrdersReportQuery,
  useGetQRImageQuery,
  useGetOrderByIdQuery,
  useValidateTicketExclusiveMutation,
  useGetSellerSummaryXlsxMutation,
  useGetOrderReferralDataQuery,
  useGetEventSalesByUserQuery,
  useGetQuickSellerSummaryQuery,
  useGetEventOrderItemsQuery,
  useCreateBatchManualOrdersMutation,
} = ordersApi;
export { ordersApi };
